import type { EventsListScrollSnapshot } from '@/lib/types/events-scroll.types';

const EVENTS_LIST_SCROLL_STORAGE_KEY = 'rise-events-list-scroll-v1';
const EVENTS_LIST_SCROLL_TTL_MS = 10 * 60 * 1000;
const APP_SCROLL_CONTAINER_ID = 'app-scroll-container';

export function getAppScrollContainer() {
  if (typeof document === 'undefined') {
    return null;
  }

  return document.getElementById(APP_SCROLL_CONTAINER_ID) as HTMLElement | null;
}

export function getCurrentHistoryIndex(): number | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const historyIndex = window.history.state?.__TSR_index;
  return typeof historyIndex === 'number' ? historyIndex : null;
}

export function getEventCardSelector(eventId: number) {
  return `[data-event-card-id="${eventId}"]`;
}

export function readEventsListScrollSnapshot(): EventsListScrollSnapshot | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(EVENTS_LIST_SCROLL_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as EventsListScrollSnapshot;
  } catch {
    return null;
  }
}

export function writeEventsListScrollSnapshot(
  snapshot: EventsListScrollSnapshot
) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(
      EVENTS_LIST_SCROLL_STORAGE_KEY,
      JSON.stringify(snapshot)
    );
  } catch {
    // Ignore storage write failures (private mode / quota / unsupported)
  }
}

export function clearEventsListScrollSnapshot() {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(EVENTS_LIST_SCROLL_STORAGE_KEY);
  } catch {
    // Ignore storage failures
  }
}

export function isValidEventsListScrollSnapshot(
  snapshot: EventsListScrollSnapshot | null,
  now: number = Date.now()
): snapshot is EventsListScrollSnapshot {
  if (!snapshot || snapshot.version !== 1 || snapshot.mode !== 'tabs') {
    return false;
  }

  if (
    !Number.isFinite(snapshot.scrollTop) ||
    snapshot.scrollTop < 0 ||
    !Number.isFinite(snapshot.loadedItemCount) ||
    snapshot.loadedItemCount < 0 ||
    !Number.isFinite(snapshot.loadedPageCount) ||
    snapshot.loadedPageCount < 0 ||
    !Number.isFinite(snapshot.timestamp)
  ) {
    return false;
  }

  if (!['upcoming', 'going', 'past'].includes(snapshot.tab)) {
    return false;
  }

  if (typeof snapshot.pathname !== 'string' || snapshot.pathname.length === 0) {
    return false;
  }

  if (
    snapshot.historyIndex !== null &&
    (!Number.isFinite(snapshot.historyIndex) || snapshot.historyIndex < 0)
  ) {
    return false;
  }

  if (
    snapshot.clickedEventId !== null &&
    (!Number.isFinite(snapshot.clickedEventId) || snapshot.clickedEventId <= 0)
  ) {
    return false;
  }

  return now - snapshot.timestamp <= EVENTS_LIST_SCROLL_TTL_MS;
}
